import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Search, Plus, X, Check } from 'lucide-react';
import { supabase } from '../../integrations/supabase/client';
import { useToast } from '../../hooks/use-toast';

interface Product {
  id: string;
  name: string;
  price: number;
  main_image?: string;
  category: string;
  in_stock: boolean;
}

interface ProductSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectProducts: (productIds: string[]) => Promise<void>;
  rankingType: 'new-arrivals' | 'top-selling';
  currentCount: number;
  maxCount: number;
}

const ProductSelectionModal: React.FC<ProductSelectionModalProps> = ({
  isOpen,
  onClose,
  onSelectProducts,
  rankingType,
  currentCount,
  maxCount
}) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const remainingSlots = maxCount - currentCount;

  useEffect(() => {
    if (isOpen) {
      fetchProducts();
    }
  }, [isOpen]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = products.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredProducts(filtered);
    } else {
      setFilteredProducts(products);
    }
  }, [searchTerm, products]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('products')
        .select('id, name, price, main_image, category, in_stock')
        .eq('in_stock', true)
        .order('name');

      if (error) throw error;
      setProducts(data || []);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch products. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleProductToggle = (productId: string) => {
    if (selectedProducts.includes(productId)) {
      setSelectedProducts(prev => prev.filter(id => id !== productId));
    } else {
      if (selectedProducts.length < remainingSlots) {
        setSelectedProducts(prev => [...prev, productId]);
      } else {
        toast({
          title: "Limit Reached",
          description: `You can only select ${remainingSlots} more products.`,
          variant: "destructive",
        });
      }
    }
  };

  const handleSubmit = async () => {
    if (selectedProducts.length === 0) {
      toast({
        title: "No Selection",
        description: "Please select at least one product to add.",
        variant: "destructive",
      });
      return;
    }

    try {
      setSubmitting(true);
      await onSelectProducts(selectedProducts);
      setSelectedProducts([]);
      onClose();
      toast({
        title: "Success",
        description: `${selectedProducts.length} product(s) added to ${rankingType === 'new-arrivals' ? 'New Arrivals' : 'Top Selling'} ranking.`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add products. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedProducts([]);
    setSearchTerm('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle>
            Add Products to {rankingType === 'new-arrivals' ? 'New Arrivals' : 'Top Selling'} Ranking
          </DialogTitle>
          <div className="flex items-center justify-between mt-2">
            <p className="text-sm text-gray-600">
              Select up to {remainingSlots} product(s) to add to the ranking
            </p>
            <Badge variant={remainingSlots > 0 ? "secondary" : "destructive"}>
              {remainingSlots} slots remaining
            </Badge>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <Input
              placeholder="Search products by name or category..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Products Grid */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
                <p className="mt-2 text-sm text-gray-600">Loading products...</p>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>No products found</p>
                <p className="text-xs">Try adjusting your search terms</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {filteredProducts.map((product) => {
                  const isSelected = selectedProducts.includes(product.id);
                  const isDisabled = !isSelected && selectedProducts.length >= remainingSlots;

                  return (
                    <Card
                      key={product.id}
                      className={`cursor-pointer transition-all ${
                        isSelected
                          ? 'ring-2 ring-blue-500 bg-blue-50'
                          : isDisabled
                          ? 'opacity-50 cursor-not-allowed'
                          : 'hover:shadow-md'
                      }`}
                      onClick={() => !isDisabled && handleProductToggle(product.id)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start space-x-3">
                          {product.main_image ? (
                            <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                              <img
                                src={product.main_image}
                                alt={product.name}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                            </div>
                          ) : (
                            <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                              <span className="text-xs text-gray-400">No Image</span>
                            </div>
                          )}

                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-medium text-gray-900 truncate">
                              {product.name}
                            </h4>
                            <p className="text-xs text-gray-500 mt-1">
                              {product.category}
                            </p>
                            <p className="text-sm font-semibold text-green-600 mt-1">
                              ₹{product.price.toLocaleString()}
                            </p>
                          </div>

                          <div className="flex-shrink-0">
                            {isSelected ? (
                              <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center">
                                <Check size={14} className="text-white" />
                              </div>
                            ) : (
                              <div className="w-6 h-6 rounded-full border-2 border-gray-300" />
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <div className="flex items-center justify-between w-full">
            <div className="text-sm text-gray-600">
              {selectedProducts.length} product(s) selected
            </div>
            <div className="flex space-x-2">
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={selectedProducts.length === 0 || submitting}
              >
                {submitting ? 'Adding...' : `Add ${selectedProducts.length} Product(s)`}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProductSelectionModal;
